import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Table,
  Form,
} from "react-bootstrap";
import { FaTrash, FaEye, FaFileImport, FaTimes, FaUpload, FaEye as FaPreview } from "react-icons/fa";
import { Link } from "react-router-dom";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../Components/Pagination";
import "./CustomerList.css";
import axios from "axios";

const StaffAttendanceList = () => {
  const parseDateDMY = (value) => {
    if (!value) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return new Date(value);
    const sep = value.includes("-") ? "-" : value.includes("/") ? "/" : null;
    if (sep) {
      const parts = value.split(sep);
      if (parts.length === 3 && parts[2].length === 4) {
        const d = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10) - 1;
        const y = parseInt(parts[2], 10);
        const dt = new Date(y, m, d);
        return isNaN(dt) ? null : dt;
      }
    }
    const dt = new Date(value);
    return isNaN(dt) ? null : dt;
  };

  const [loading, setLoading] = useState(true);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [attendanceList, setAttendanceList] = useState([]);

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchAttendance();
  }, []);

  const fetchAttendance = () => {
    setLoading(true);
    axios
      .post(`${API_BASE_URL}attendance_list.php`)
      .then((response) => {
        if (response.data.head.code === 200 && Array.isArray(response.data.body)) {
          setAttendanceList(response.data.body);
        } else {
          console.error("Error Fetching Data:", response.data.head.msg);
          setAttendanceList([]);
        }
      })
      .catch((error) => {
        console.error("API Error:", error);
        setAttendanceList([]);
      })
      .finally(() => setLoading(false));
  };

  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const handleRemoveItem = (att_id) => {
    axios
      .post(`${API_BASE_URL}attendance_delete.php`, { att_id })
      .then((response) => {
        if (response.data.head.code === 200) {
          window.location.reload();
        } else {
          console.error("Error Fetching Data:", response.data.head.msg);
        }
      })
      .catch((error) => console.error("API Error:", error));
  };

  // const formatDateDuration = (att) => `${att.att_date}`;
  const formatDateDuration = (att) => {
  if (!att.att_date) return "";

  const [year, month, day] = att.att_date.split("-");
  return `${day}-${month}-${year}`;
};

const formatTime = (dateTime) => {
  if (!dateTime) return "";
  return dateTime.split(" ")[1];
};

  // ── Import modal handlers ──────────────────────────────────────
  const openImportModal = () => {
    setShowImportModal(true);
    setImportFile(null);
    setPreviewData([]);
    setShowPreview(false);
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setPreviewData([]);
    setShowPreview(false);
  };

  // const handleFileChange = (e) => {
  //   setImportFile(e.target.files[0]);
  //   setPreviewData([]);
  //   setShowPreview(false);
  // };
  const handleFileChange = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    setImportFile(file);
    setPreviewData([]);
    setShowPreview(false);

    const formData = new FormData();
    formData.append("file", file);

    try {
      setImportLoading(true);

      const response = await axios.post(
        `${API_BASE_URL}attendancePreview.php`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status) {
        setPreviewData(response.data.data || []);
        setShowPreview(true); // Automatically show preview
      } else {
        alert(response.data.message || "Preview failed.");
      }
    } catch (err) {
      console.error(err);
      alert("Preview failed. Please check the file and try again.");
    } finally {
      setImportLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!importFile) {
      alert("Please select an Excel file first.");
      return;
    }
    const formData = new FormData();
    formData.append("file", importFile);
    try {
      setImportLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}attendancePreview.php`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response.data.status) {
        setPreviewData(response.data.data || []);
        setShowPreview(true);
      } else {
        alert(response.data.message || "Preview failed.");
      }
    } catch (err) {
      console.error(err);
      alert("Preview failed. Please check the file and try again.");
    } finally {
      setImportLoading(false);
    }
  };

  const saveAttendance = async () => {
    try {
      setSaveLoading(true);
      const response = await axios.post(`${API_BASE_URL}saveAttendance.php`, {
        attendance: previewData,
      });
      alert(response.data.message || "Saved successfully!");
      closeImportModal();
      fetchAttendance();
    } catch (err) {
      console.error(err);
      alert("Save failed. Please try again.");
    } finally {
      setSaveLoading(false);
    }
  };
  // ──────────────────────────────────────────────────────────────

  const filteredAttendance = attendanceList.filter((item) => {
    const q = search.trim().toLowerCase();
    const matchesSearch =
      !q ||
      String(item.att_id).includes(q) ||
      (item.user_name && item.user_name.toLowerCase().includes(q)) ||
      (item.desig_name && item.desig_name.toLowerCase().includes(q)) ||
      (item.depart_name && item.depart_name.toLowerCase().includes(q)) ||
      (item.att_date && item.att_date.toLowerCase().includes(q)) ||
      (item.att_time && item.att_time.toLowerCase().includes(q)) ||
      (item.status && item.status.toLowerCase().includes(q));

    if (!matchesSearch) return false;
    if (fromDate || toDate) {
      const reqDate = parseDateDMY(item.att_date);
      if (!reqDate) return false;
      if (fromDate && reqDate < new Date(fromDate + "T00:00:00")) return false;
      if (toDate && reqDate > new Date(toDate + "T23:59:59")) return false;
    }
    return true;
  });

  const { currentPage, totalPages, currentItems, handlePageChange, indexOfFirstItem } =
    usePagination(filteredAttendance, 25);

  // ── Inline styles ──────────────────────────────────────────────
  const overlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    zIndex: 1050,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
  };

  const modalCardStyle = {
    background: "var(--bs-body-bg, #fff)",
    borderRadius: "12px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    width: "100%",
    maxWidth: "780px",
    maxHeight: "90vh",
    overflowY: "auto",
    padding: "1.5rem",
  };

  const importBtnStyle = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "14px",
    fontWeight: 500,
    padding: "7px 16px",
    borderRadius: "8px",
    border: "1.5px solid #198754",
    background: "#198754",
    color: "#fff",
    cursor: "pointer",
    transition: "background 0.15s",
  };

  const dropZoneStyle = {
    border: "2px dashed #ced4da",
    borderRadius: "10px",
    padding: "2rem 1rem",
    textAlign: "center",
    cursor: "pointer",
    background: importFile ? "#f0fff4" : "#f8f9fa",
    transition: "border-color 0.2s, background 0.2s",
    marginBottom: "1rem",
  };

  const badgeStyle = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "present")
      return { background: "#d1fae5", color: "#065f46", fontSize: "12px", padding: "2px 10px", borderRadius: "20px", fontWeight: 500 };
    if (s === "absent")
      return { background: "#fee2e2", color: "#991b1b", fontSize: "12px", padding: "2px 10px", borderRadius: "20px", fontWeight: 500 };
    return { background: "#f3f4f6", color: "#374151", fontSize: "12px", padding: "2px 10px", borderRadius: "20px", fontWeight: 500 };
  };
  // ──────────────────────────────────────────────────────────────

  return (
    <Container fluid className="my-4 outer-container">
      {/* ── Header row: title + import btn ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem", flexWrap: "wrap", gap: "10px" }}>
        <h2 className="text-center mb-0" style={{ flex: 1 }}>👥 Staff Attendance Records</h2>
        <button style={importBtnStyle} onClick={openImportModal}>
          <FaFileImport size={15} /> Import Attendance
        </button>
      </div>

      {/* Filters row (copied search/date UI from OdList) */}
      <Row className="mb-3">
        <Col md={4} className="mb-2">
          <Form.Label>Search</Form.Label>
          <Form.Control
            placeholder="Search by name, designation, department, time, status..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>

        <Col md={3} className="mb-2">
          <Form.Label>From</Form.Label>
          <Form.Control
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            placeholder="From Date"
          />
        </Col>

        <Col md={3} className="mb-2">
          <Form.Label>To</Form.Label>
          <Form.Control
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            placeholder="To Date"
          />
        </Col>

        <Col md={2} className="d-flex mt-4">
          <Button
            className="btn-top"
            onClick={() => {
              setSearch("");
              setFromDate("");
              setToDate("");
            }}
          >
            Clear Filters
          </Button>
        </Col>
      </Row>

      {/* ── Table ── */}
      <div className="table-pagination-wrapper">
        <div className="table-pagination-content">
          <Table bordered responsive className="table-striped customer-table">
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Name</th>
                <th>Designation</th>
                <th>Department</th>
                <th>Date</th>
                <th>Time</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">Loading...</td>
                </tr>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">No attendance records found</td>
                </tr>
              ) : (
                currentItems.map((item, i) => (
                  <tr key={item.att_id}>
                    <td>
                      {indexOfFirstItem + (i+1)}
                      {/* {currentItems.length - (indexOfFirstItem + i)} */}
                      </td>
                    <td>{item.user_name}</td>
                    <td>{item.desig_name}</td>
                    <td>{item.depart_name}</td>
                    <td>{formatDateDuration(item)}</td>
                    {/* <td>{item.att_time}</td> */}
                    <td>{formatTime(item.att_time)}</td>
                    <td>
                      <span style={badgeStyle(item.status)}>{item.status}</span>
                    </td>
                    <td className="action-icons">
                      <Link to={`/viewattendance/${item.att_id}`}>
                        <FaEye className="view-icon" />
                      </Link>
                      <FaTrash
                        className="delete-icon"
                        onClick={() => {
                          if (window.confirm("Are you sure you want to delete this?")) {
                            handleRemoveItem(item.att_id);
                          }
                        }}
                        style={{ cursor: "pointer" }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {/* ── Import Attendance Modal ── */}
      {showImportModal && (
        <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) closeImportModal(); }}>
          <div style={modalCardStyle}>
            {/* Modal header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem" }}>
              <h5 style={{ margin: 0, fontWeight: 600, fontSize: "17px" }}>
                <FaFileImport style={{ marginRight: "8px", color: "#198754" }} />
                Import Attendance
              </h5>
              <button
                onClick={closeImportModal}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px", color: "#6c757d", lineHeight: 1 }}
              >
                <FaTimes />
              </button>
            </div>

            {/* Drop zone / file picker */}
            <div
              style={dropZoneStyle}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              <FaUpload size={28} color={importFile ? "#198754" : "#adb5bd"} />
              <p style={{ margin: "10px 0 4px", fontWeight: 500, color: importFile ? "#198754" : "#495057" }}>
                {importFile ? importFile.name : "Click to select Excel file"}
              </p>
              <p style={{ margin: 0, fontSize: "12px", color: "#6c757d" }}>
                {importFile ? `${(importFile.size / 1024).toFixed(1)} KB` : "Supports .xls and .xlsx formats"}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xls,.xlsx"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </div>

            {/* Action buttons */}
            <div style={{ display: "flex", gap: "10px", marginBottom: showPreview ? "1.25rem" : 0 }}>
              {/* <Button
                variant="primary"
                onClick={handlePreview}
                disabled={importLoading || !importFile}
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <FaPreview size={13} />
                {importLoading ? "Loading Preview..." : "Preview"}
              </Button> */}

              {showPreview && previewData.length > 0 && (
                <Button
                  variant="success"
                  onClick={saveAttendance}
                  disabled={saveLoading}
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  {saveLoading ? "Saving..." : `Save ${previewData.length} Records`}
                </Button>
              )}
              {showPreview && previewData.length > 0 && (
                <Button variant="outline-secondary" onClick={closeImportModal}>
                  Cancel
                </Button>
              )}
            </div>

            {/* Preview table */}
            {showPreview && (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
                  <span style={{ fontWeight: 500, fontSize: "14px" }}>
                    Preview —{" "}
                    <span style={{ color: "#198754" }}>{previewData.length} records</span> found
                  </span>
                </div>
                <div style={{ overflowX: "auto", borderRadius: "8px", border: "1px solid #dee2e6" }}>
                  <Table bordered striped size="sm" style={{ marginBottom: 0, fontSize: "13px" }}>
                    <thead style={{ background: "#f8f9fa" }}>
                      <tr>
                        <th>S.No</th>
                        <th>Emp Code</th>
                        <th>Name</th>
                        <th>Date</th>
                        <th>In Time</th>
                        <th>Out Time</th>
                        <th>Hours</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((row, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{row.emp_code}</td>
                          <td>{row.emp_name}</td>
                          <td>{row.date}</td>
                          <td>{row.in_time}</td>
                          <td>{row.out_time}</td>
                          <td>{row.work_hours}</td>
                          <td>
                            <span style={badgeStyle(row.status)}>{row.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Container>
  );
};

export default StaffAttendanceList;